/**
 * The phase bar — which stage of the project every screen below is looking at
 * (`KLQ-20260827-002` §"Phase bar behaviour").
 *
 *     [ All ]  AŠ   SZ (DÚR)   ● PS (DSP)   VP (DRS)     [+ add phase]     STN · SK 2025
 *      Overview · Drawings · Reviews · Tasks · Activity · People · Settings
 *
 * It sits **above** `ProjectTabs`, and it is drawn whenever the project context is — including
 * for a project with a single phase. That is deliberate and it is the one rule here worth
 * arguing for: a bar that hid itself when there was nothing to switch between would also hide
 * *"+ add phase"*, so the studio that most needs to discover phases is the only one never
 * shown that they exist.
 *
 * **THE TWO MARKS ARE DIFFERENT FACTS AND ARE DRAWN DIFFERENTLY.** This is the spec's emphatic
 * one, and it is easy to collapse by accident:
 *
 *   - **Selection** is the viewer's, private to their browser, and it changes what is on the
 *     screen. It is drawn as the chip's *surface* — `--neutral-100` ground, ink text, ink
 *     hairline, `STYLE.md` §3's selected chip (an ink fill until 2026-10-24, when it was found
 *     to be the first ink object on thirteen project screens — `KLQ-20261024-001` §B).
 *   - **Current** is the project's, held by the server, seen identically by everybody on the
 *     project, and it changes nothing on screen: it is where the next drawing gets filed. It
 *     is drawn as a small filled disc *inside* the chip, in accent, with its meaning in the
 *     accessible name.
 *
 * They compose — the current phase can also be the selected one — which is exactly why one of
 * them cannot be a background and the other a border. A person must be able to answer "which
 * am I looking at" and "where will my next drawing land" from the same row without learning a
 * legend.
 *
 * **Narrow viewports keep the chips and scroll them** (owner, 2026-09-21). Until that date they
 * collapsed into a native `<select>`, and the argument for it was a good one: a select always
 * shows what is selected, where a scrolling row lets the selection slide out of sight. What
 * that argument missed is that the phase row is not a form field — it is *where you are*, the
 * same fact the tab row below it carries, and a phone that draws one as a chip row and the
 * other as a dropdown reads as two unrelated controls. The objection is answered instead of
 * conceded: `lib/scrollRow` pulls the selected chip to the middle of the row on every change,
 * so it is never the thing that scrolled away, and the ladder stays legible as a ladder — you
 * can see that DSP comes after DÚR, which a dropdown only tells you once it is open.
 *
 * *"+ add phase"* keeps its own place outside the scroller (`.phaseAddNarrow`, icon only) so it
 * cannot be pushed off the right edge by a long ladder. The per-chip menus still go: renaming
 * and archiving a phase are administration, they have a home in Project settings, and they are
 * not what somebody on a phone came here to do.
 *
 * **It has no copy of its own.** Every word arrives as a prop, like `Menu`, `Slicer` and
 * `ChipSelect` — so this file adds no keys to the `app` namespace and cannot disagree with the
 * vocabulary of the screen family that draws it. `features/projects/ProjectPhaseBar` is the
 * connected half.
 */

import { Plus } from 'lucide-react';

import { Menu } from '@/components';
import type { MenuItem } from '@/components';
import { useCenterSelected } from '@/lib/scrollRow';

import styles from './Chrome.module.css';

export interface PhaseChip {
  readonly id: string;
  /** Already composed — *"Projekt stavby (DSP)"*. `features/projects/phase.ts:phaseLabel`. */
  readonly label: string;
  /** Greys out, and drops out of every picker that offers a phase for new work. */
  readonly archived: boolean;
  /** The project's default for new drawings. **Not** the viewer's selection. */
  readonly isCurrent: boolean;
  /** Rename, set as current, archive. Empty means this caller administers nothing. */
  readonly menu: readonly MenuItem[];
}

export interface PhaseBarProps {
  readonly chips: readonly PhaseChip[];
  /** `null` is *All phases* — the absence of a filter, never a phase of its own. */
  readonly selectedId: string | null;
  readonly onSelect: (phaseId: string | null) => void;
  /** The row's accessible name. */
  readonly label: string;
  readonly allLabel: string;
  /** What the disc means, spelled out for a reader who cannot see it. */
  readonly currentLabel: string;
  /** Appended to an archived chip's accessible name. */
  readonly archivedLabel: string;
  /** Absent for a caller who may not change the phase set. */
  readonly addLabel?: string | undefined;
  readonly onAdd?: (() => void) | undefined;
  /** The accessible name of one chip's overflow trigger, given that chip's label. */
  readonly menuLabel: (label: string) => string;
  /** `STN · SK — zákon 25/2025…`. Read-only here; the pin is changed in Project settings. */
  readonly norm?: string | undefined;
}

export function PhaseBar({
  chips,
  selectedId,
  onSelect,
  label,
  allLabel,
  currentLabel,
  archivedLabel,
  addLabel,
  onAdd,
  menuLabel,
  norm,
}: PhaseBarProps) {
  /**
   * The selected chip is pulled to the middle of the row whenever it changes — see
   * `lib/scrollRow`, and the header above for why this row scrolls on a phone instead of
   * becoming a dropdown. `''` stands for *All*, which is a selection like any other here.
   */
  const { scrollerRef, selectedRef } = useCenterSelected<
    HTMLDivElement,
    HTMLButtonElement
  >(selectedId ?? '');

  return (
    <div className={styles.phaseBar} aria-label={label} role="group">
      <div ref={scrollerRef} className={styles.phaseChips}>
        <button
          ref={selectedId === null ? selectedRef : undefined}
          type="button"
          className={[styles.phaseChip, selectedId === null ? styles.phaseChipSelected : '']
            .filter(Boolean)
            .join(' ')}
          aria-pressed={selectedId === null}
          onClick={() => {
            onSelect(null);
          }}
        >
          {allLabel}
        </button>

        {chips.map((chip) => {
          const selected = chip.id === selectedId;
          return (
            <span
              key={chip.id}
              className={[styles.phaseSlot, chip.archived ? styles.phaseSlotArchived : '']
                .filter(Boolean)
                .join(' ')}
            >
              <button
                ref={selected ? selectedRef : undefined}
                type="button"
                className={[styles.phaseChip, selected ? styles.phaseChipSelected : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={selected}
                onClick={() => {
                  onSelect(chip.id);
                }}
              >
                {chip.isCurrent ? (
                  <>
                    <span className={styles.phaseCurrentMark} aria-hidden />
                    <span className="sr-only">{`${currentLabel} `}</span>
                  </>
                ) : null}
                {chip.label}
                {chip.archived ? <span className="sr-only">{` ${archivedLabel}`}</span> : null}
              </button>
              {chip.menu.length === 0 ? null : (
                /* Administration, and not on a phone — the wrapper is what the stylesheet
                   hides under 720px. See the header. */
                <span className={styles.phaseSlotMenu}>
                  <Menu label={menuLabel(chip.label)} items={chip.menu} align="start" />
                </span>
              )}
            </span>
          );
        })}

        {addLabel === undefined || onAdd === undefined ? null : (
          <button type="button" className={styles.phaseAdd} onClick={onAdd}>
            <Plus size={14} strokeWidth={1.5} aria-hidden />
            {addLabel}
          </button>
        )}
      </div>

      {/* The narrow form of *+ add phase*: outside the scroller, so a ten-rung ladder cannot
          push it off the right edge. Its wide twin lives inside the row above. */}
      {addLabel === undefined || onAdd === undefined ? null : (
        <button
          type="button"
          className={styles.phaseAddNarrow}
          aria-label={addLabel}
          onClick={onAdd}
        >
          <Plus size={16} strokeWidth={1.5} aria-hidden />
        </button>
      )}

      <span className={styles.phaseBarSpacer} />

      {norm === undefined ? null : (
        <span className={styles.phaseNorm} title={norm}>
          {norm}
        </span>
      )}
    </div>
  );
}
